import { useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeInternalRedirect, APP_ENTRY_ROUTE } from "@/lib/access-flow";

/**
 * Hook centralizado para fazer signOut e voltar para a Landing.
 *
 * Garante:
 * - Idempotência: chamadas concorrentes (clique duplo, evento disparado
 *   duas vezes) reutilizam a mesma promise — telemetria, signOut e
 *   navigate só executam UMA vez.
 * - Cancelamento de queries em vôo + limpeza do cache do React Query
 *   (evita refetch com token expirado e vazamento entre contas).
 * - Sanitização do destino (sempre rota interna).
 * - Tracking fire-and-forget no funil `auth.sign_out` em `user_events`,
 *   incluindo `role` (user/venue/admin) e `from_path` para segmentação.
 *   Suprimido quando não há sessão (evita linhas órfãs).
 * - Resiliência: se `signOut()` falhar, ainda assim limpa cache e navega
 *   para a Landing — o usuário NUNCA fica preso em uma tela autenticada.
 * - Navegação com `replace` para não poluir o histórico.
 *
 * Use este hook em vez de chamar `signOut()` diretamente nas telas.
 */
export const useSignOutAndGoHome = () => {
  const { signOut, user } = useAuth();
  const { isAdmin, isVenueOwner } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const inFlightRef = useRef<Promise<string> | null>(null);

  return useCallback((): Promise<string> => {
    if (inFlightRef.current) return inFlightRef.current;

    const run = async (): Promise<string> => {
      const role: "admin" | "venue" | "user" = isAdmin
        ? "admin"
        : isVenueOwner
        ? "venue"
        : "user";
      const fromPath = location?.pathname ?? null;

      // Métrica de funil — fire-and-forget. Falhas de rede/tabela NÃO devem
      // bloquear nem reverter o signOut.
      try {
        const userId = user?.id;
        if (userId) {
          void supabase
            .from("user_events" as any)
            .insert({
              user_id: userId,
              event_name: "auth.sign_out",
              event_type: "auth.sign_out",
              metadata: { role, from_path: fromPath },
            } as any)
            .then(() => void 0, () => void 0);
        }
      } catch {
        /* noop */
      }

      // Cancela queries em vôo ANTES de limpar o cache para que nenhum
      // refetch dispare com o token que está prestes a ser invalidado.
      try {
        await queryClient.cancelQueries();
      } catch {
        /* noop */
      }

      let destination: string | undefined;
      try {
        destination = await signOut();
      } catch {
        // Se o signOut falhar (rede, sessão já expirada etc.) ainda assim
        // forçamos a saída local: limpa cache, leva o usuário pra Landing.
        destination = APP_ENTRY_ROUTE;
      }

      try {
        queryClient.clear();
      } catch {
        /* noop */
      }

      const safe = sanitizeInternalRedirect(destination) ?? APP_ENTRY_ROUTE;
      navigate(safe, { replace: true });
      return safe;
    };

    const promise = run().finally(() => {
      // Libera a guarda apenas após a navegação concluir; novas chamadas
      // após esse ponto referem-se a uma sessão diferente (re-login).
      inFlightRef.current = null;
    });
    inFlightRef.current = promise;
    return promise;
  }, [signOut, navigate, queryClient, user?.id, isAdmin, isVenueOwner, location?.pathname]);
};
